import Bugsnag from "@bugsnag/js";
import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import axiosInstance from "../../axiosInstance";
import URL from "../../urls";
import { TYPE_PSYCHIATRIST } from "../../variable";

import FlexBox from "../common/FlexBox";
import { Body2, H4 } from "../common/Headings";
import { Modal } from "../common/Modal";
import {
  DAVYS_GREY_300,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  white,
} from "../common/colors";
import {
  EditingCustomizationPanel,
  LeftSummarySection,
  ModalFooterSection,
  SavedCustomizationPanel,
  SummaryTypeSelector,
} from "./SessionCustomisation";
import { addToast } from "../../Store/Actions";

const ModalWrapper = styled(FlexBox)`
  background: ${white};
  flex: 1;
  border-radius: 1rem;
  overflow: hidden;
  max-height: 100vh;
`;

const VerticalDivider = styled.div`
  width: 1px;
  background: ${DAVYS_GREY_300};
`;

const HeaderSection = styled(FlexBox)`
  flex-direction: column;
  border-bottom: 1px solid ${DAVYS_GREY_300};
  padding: 1rem 1.5rem;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${DAVYS_GREY_800};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const ContentRow = styled(FlexBox)`
  flex: 1;
  overflow: hidden;
  min-height: 0;
  padding: 0;
  border-top: 1px solid ${DAVYS_GREY_300};
`;

const RightSection = styled(FlexBox)`
  flex: 1;
  flex-direction: column;
  padding: 1.5rem;
  gap: 1rem;
  min-height: 0;
  overflow: scroll;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SessionCustomisationModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const usertype = useSelector(state => state?.auth?.user?.usertype);
  const isPsychiatrist = usertype === TYPE_PSYCHIATRIST;

  const [summaryType, setSummaryType] = useState("session_note_generation");
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [promptInput, setPromptInput] = useState("");
  const [validationResult, setValidationResult] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [hasPromptSent, setHasPromptSent] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [promptCustomizationId, setPromptCustomizationId] = useState(null);

  const getCustomizationFromResponse = response => {
    const customization = response?.data?.data;

    return {
      id: customization?.id ?? null,
      additionalPrompt: customization?.additional_prompt ?? "",
      action: customization?.action ?? "",
      status: customization?.status ?? "",
    };
  };

  useEffect(() => {
    if (isOpen) {
      setPromptInput("");
      setValidationResult("");
      setErrorMessage("");
      setHasPromptSent(false);
      setIsReviewing(false);
      setHasError(false);
      setIsSuccess(false);
      setIsEditing(false);
      setShowRemoveConfirm(false);
      setSummaryType("session_note_generation");
    }
  }, [isOpen]);

  const showEditingView = !additionalPrompt || isEditing;
  const isPromptSendEnabled = Boolean(promptInput.trim()) && !isReviewing;

  const handleSendPrompt = async () => {
    setIsReviewing(true);
    setHasError(false);
    setIsSuccess(false);
    setErrorMessage("");
    setValidationResult("");

    try {
      const response = await axiosInstance.post(
        URL.customizationPromptValidate,
        {
          summary_type: summaryType,
          provider_customized_prompt: promptInput.trim(),
        }
      );

      const result = response?.data?.data?.result ?? "";

      setValidationResult(result);
      setHasPromptSent(true);
      setIsSuccess(true);
    } catch (error) {
      setHasError(true);

      setErrorMessage(
        error?.response?.data?.data?.error_message ||
          "Unable to validate customisation."
      );

      Bugsnag.notify(error);
    } finally {
      setIsReviewing(false);
    }
  };

  const handleSave = async () => {
    if (!isSuccess) return;

    const payload = {
      prompt_action: summaryType,
      additional_prompt: validationResult,
    };

    try {
      const response = promptCustomizationId
        ? await axiosInstance.patch(
            `${URL.aiChatPrompts}/${promptCustomizationId}`,
            payload
          )
        : await axiosInstance.post(URL.aiChatPrompts, payload);

      const { id, additionalPrompt: savedPrompt } =
        getCustomizationFromResponse(response);

      setPromptCustomizationId(id ?? promptCustomizationId);
      setAdditionalPrompt(savedPrompt);
      setPromptInput(savedPrompt);

      setHasPromptSent(false);
      setIsSuccess(false);
      setHasError(false);
      setShowRemoveConfirm(false);
      setIsEditing(false);
      dispatch(
        addToast({
          msg: `Customization ${promptCustomizationId ? "updated" : "created"} successfully.`,
          autoDismiss: true,
          appearance: "success",
          showBottomToastOnMobile: true,
        })
      );
    } catch (error) {
      setHasError(true);
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Failed to save customization. Please try again.",
          autoDismiss: true,
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setPromptInput(additionalPrompt);
    setValidationResult("");
    setErrorMessage("");
    setHasPromptSent(false);
    setHasError(false);
    setIsSuccess(false);
    setShowRemoveConfirm(false);
  };

  const resetCustomizationState = () => {
    setPromptCustomizationId(null);
    setAdditionalPrompt("");
    setValidationResult("");
    setErrorMessage("");
    setIsEditing(false);
    setShowRemoveConfirm(false);
    setPromptInput("");
    setHasPromptSent(false);
    setHasError(false);
    setIsSuccess(false);
  };

  const handleRemoveCustomization = async () => {
    try {
      if (!promptCustomizationId) {
        resetCustomizationState();
        return;
      }

      await axiosInstance.delete(
        `${URL.aiChatPrompts}/${promptCustomizationId}`,
        {
          params: {
            prompt_action: summaryType,
          },
        }
      );

      resetCustomizationState();
      dispatch(
        addToast({
          msg: "Customization removed successfully.",
          autoDismiss: true,
          appearance: "success",
          showBottomToastOnMobile: true,
        })
      );
    } catch (error) {
      setHasError(true);
      Bugsnag.notify(error);
      dispatch(
        addToast({
          msg: "Failed to remove customization. Please try again.",
          autoDismiss: true,
          appearance: "error",
          showBottomToastOnMobile: true,
        })
      );
    }
  };

  const summaryTypeOptions = [
    { label: "Session Note", slug: "session_note_generation" },
    { label: "Intake Report", slug: "intake_report_generation" },
  ];

  const normalizedSummaryType = summaryType.replace("_generation", "");

  const summaryTypeDescriptionKey = `${normalizedSummaryType}_${
    isPsychiatrist ? "psychiatrist" : "therapist"
  }`;

  useEffect(() => {
    const fetchActiveCustomization = async () => {
      if (!isOpen || !summaryType) return;

      try {
        const response = await axiosInstance.get(URL.aiChatPrompts, {
          params: {
            prompt_action: summaryType,
          },
        });

        const { id, additionalPrompt } = getCustomizationFromResponse(response);

        setPromptCustomizationId(id);
        setAdditionalPrompt(additionalPrompt);
        setPromptInput(additionalPrompt);
      } catch (error) {
        if (error?.response?.status === 404) {
          resetCustomizationState();
          return;
        }

        setPromptCustomizationId(null);
        setAdditionalPrompt("");
        setPromptInput("");

        Bugsnag.notify(error);
      }
    };

    fetchActiveCustomization();
  }, [isOpen, summaryType]);

  useEffect(() => {
    setIsSuccess(false);
    setHasPromptSent(false);
  }, [promptInput]);

  const summaryTypeDescriptions = {
    session_note_therapist:
      "The AI creates structured session notes from your transcripts or raw notes. It captures key themes, client statements, and clinical observations — formatted and organised, using only what you provide in the session. It will not add interpretations, suggest diagnoses, or pull in any outside information.",
    session_note_psychiatrist:
      "The AI creates structured session notes from your transcripts or raw notes. It organises the session into a clear psychiatric format — covering presenting concerns, mental state observations, medication discussions, and relevant client statements — using only what you provide. It will not add interpretations, suggest diagnoses, or pull in any outside information.",
    intake_report_therapist:
      "The AI generates a complete intake report from your first session transcript. It extracts and organises information across all key sections — demographics, presenting concerns, history, family background, risk, goals, and treatment plan — using only what was discussed in the session. Any section with no data from the session will be clearly marked rather than assumed.",
    intake_report_psychiatrist:
      "The AI generates a complete intake report from your first session transcript. It extracts and organises information across all key sections — demographics, presenting concerns, psychiatric and medical history, family background, risk assessment, MSE, goals, and treatment plan — using only what was discussed in the session. Any section with no data from the session will be clearly marked rather than assumed.",
  };

  if (!isOpen) return null;

  return (
    <Modal
      L
      height="100vh"
      togglePopup={onClose}
      maxHeight="100vh"
      backgroundClickDisabled
    >
      <ModalWrapper column>
        <HeaderSection>
          <FlexBox justify="space-between" align="center">
            <H4 bold>Customise Amaha AI</H4>
            <CloseButton type="button" onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
          </FlexBox>
          <Body2 color={DAVYS_GREY_700} lineHeight="1.5rem">
            Choose how your AI-generated summaries look and feel, from format to
            length and focus. Update anytime to match your note-taking style.
          </Body2>
        </HeaderSection>

        <SummaryTypeSelector
          summaryTypeOptions={summaryTypeOptions}
          setSummaryType={setSummaryType}
          hasActiveCustomization={Boolean(additionalPrompt)}
        />

        <ContentRow>
          <LeftSummarySection
            summaryText={summaryTypeDescriptions[summaryTypeDescriptionKey]}
          />

          <VerticalDivider />

          <RightSection>
            {showEditingView ? (
              <EditingCustomizationPanel
                isReviewing={isReviewing}
                hasError={hasError}
                isSuccess={isSuccess}
                promptInput={promptInput}
                validationResult={validationResult}
                errorMessage={errorMessage}
                setPromptInput={setPromptInput}
                isPromptSendEnabled={isPromptSendEnabled}
                handleSendPrompt={handleSendPrompt}
              />
            ) : (
              <SavedCustomizationPanel
                additionalPrompt={additionalPrompt}
                handleEdit={handleEdit}
                setShowRemoveConfirm={setShowRemoveConfirm}
              />
            )}
          </RightSection>
        </ContentRow>

        <ModalFooterSection
          showRemoveConfirm={showRemoveConfirm}
          handleRemoveCustomization={handleRemoveCustomization}
          showEditingView={showEditingView}
          hasPromptSent={hasPromptSent}
          isSuccess={isSuccess}
          handleSave={handleSave}
        />
      </ModalWrapper>
    </Modal>
  );
};

export default SessionCustomisationModal;
